// AboutWithFaqPage.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, BoltIcon, LightBulbIcon, UsersIcon, StarIcon, CheckCircleIcon, ChevronDownIcon, QuestionMarkCircleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

import abbasImg from '../assets/team/abbas.png';
import mensahImg from '../assets/team/mensah.png';
import martinImg from '../assets/team/martin.png';
import agbodjiImg from '../assets/team/agbodji.jpeg';
import logo from '../assets/images/vadom.jpeg';

const AboutWithFaqPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  // Données de l'équipe
  const team = [
  { name: 'Abbas YOROU', role: 'Co-fondateur', image: abbasImg },
  { name: 'Mensah Koffi EATSU', role: 'CEO & Co-fondateur', image: mensahImg },
  { name: 'Martin DOTSEVI', role: 'Assistant Chef de Projet', image: martinImg },
  { name: 'AGBODJI Kossi Inésse', role: 'Designer Graphiste', image: agbodjiImg },
];

  // Statistiques
  const stats = [
    { number: '1500+', label: 'Projets réalisés', icon: CheckCircleIcon },
    { number: '250+', label: 'Smarts actifs', icon: UsersIcon },
    { number: '700+', label: 'Clients satisfaits', icon: StarIcon },
    { number: '4.8★', label: 'Note moyenne', icon: StarIcon },
  ];

  // Valeurs
  const values = [
    { icon: ShieldCheckIcon, title: 'Confiance', desc: 'Chaque Smart est vérifié. Chaque transaction est sécurisée. La confiance est au cœur de notre plateforme.' },
    { icon: BoltIcon, title: 'Rapidité', desc: 'Trouvez un prestataire disponible en quelques minutes. Nous valorisons votre temps.' },
    { icon: LightBulbIcon, title: 'Innovation', desc: 'Nous utilisons la technologie pour résoudre des problèmes concrets du quotidien africain.' },
  ];

  // FAQ
  const faqs = [
    { question: "L'application est-elle sécurisée ?", answer: "Oui. VADOM utilise des protocoles de chiffrement avancés pour protéger vos données. Toutes les transactions financières passent par des passerelles sécurisées certifiées (PCI-DSS)." },
    { question: "Comment vérifiez-vous les prestataires (Smarts) ?", answer: "Chaque Smart passe par un processus de vérification rigoureux : vérification d'identité, validation des compétences et vérification des références." },
    { question: "Quels sont les avantages pour devenir Smart ?", answer: "Visibilité auprès de milliers de clients, gestion d'agenda intégrée, paiements sécurisés, tableau de bord analytique et support prioritaire." },
    { question: "L'application est-elle disponible en dehors du Togo ?", answer: "Actuellement disponible à Lomé. Expansion en cours vers Bénin, Côte d'Ivoire et Ghana." },
    { question: "Comment se passe le paiement ?", answer: "Paiement sécurisé via mobile money (T Money, Flooz), carte bancaire ou espèces." },
    { question: "Comment devenir Smart VADOM ?", answer: "Remplissez le formulaire sur la page 'Devenir Smart', validation sous 48h, configuration gratuite." },
  ];

  return (
    <div className="overflow-hidden bg-[#0a0a0a]">
      {/* ============ SECTION ABOUT ============ */}
      
      {/* Hero About */}
      <section className="relative bg-gradient-to-br from-[#252525] to-[#374151]/30 text-white py-24 lg:py-32 overflow-hidden">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
            >
              <span className="text-sm font-medium">À propos de VADOM</span>
            </motion.div>
            
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-display font-bold mb-6 leading-tight">
              Notre mission : simplifier l'accès aux services en Afrique
            </h1>
            <p className="text-xl text-white/90 max-w-2xl">
              VADOM est une startup togolaise innovante qui révolutionne l'accès aux services 
              du quotidien en Afrique de l'Ouest.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
                  fill="white"/>
          </svg>
        </div>
      </section>

      {/* Qui sommes-nous */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-4">
                <span className="text-[#F97316] text-sm font-medium">Notre histoire</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-display font-bold mb-6 text-white">
                Qui <span className="text-[#F97316]">sommes-nous</span> ?
              </h2>
              <p className="text-[#AAAAAA] text-lg mb-4 leading-relaxed">
                Fondée au Togo, VADOM est née d'un constat simple : trouver un artisan fiable 
                et disponible rapidement est un vrai défi en Afrique de l'Ouest.
              </p>
              <p className="text-[#AAAAAA] text-lg mb-6 leading-relaxed">
                Notre plateforme met en relation des clients exigeants avec des prestataires 
                qualifiés et vérifiés. Nous créons un écosystème de confiance où chacun y gagne.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mt-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center p-4 bg-[#111111] rounded-xl border border-[#252525]"
                  >
                    <stat.icon className="h-6 w-6 text-[#F97316] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{stat.number}</p>
                    <p className="text-sm text-[#AAAAAA]">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute mt-10 bg-gradient-to-br from-[#1a1a1a] to-[#111111] rounded-3xl p-8 border border-[#252525]">
                <div className="text-center">
                 <img
                    src={logo}
                    alt="VADOM Logo"
                    className="w-24 h-25 mb-4 mx-auto rounded-2xl"
                  />
                  <h3 className="text-2xl font-bold text-white mb-4">VADOM</h3>
                  <p className="text-[#AAAAAA]">La révolution des services à domicile en Afrique</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nos valeurs */}
      <section className="py-20 bg-[#111111]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-4">
              <span className="text-[#F97316] text-sm font-medium">Nos piliers</span>
            </div>
            <h2 className="section-title">
              Nos <span className="text-[#F97316]">valeurs</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-[#0a0a0a] rounded-2xl p-6 text-center border border-[#252525] hover:border-[#F97316]/30 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#F97316]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-8 w-8 text-[#F97316]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-[#AAAAAA] leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Notre équipe */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-4">
              <span className="text-[#F97316] text-sm font-medium">L'équipe</span>
            </div>
            <h2 className="section-title">
              Notre <span className="text-[#F97316]">équipe</span>
            </h2>
            <p className="text-[#AAAAAA] mt-4 max-w-2xl mx-auto">
              Une équipe passionnée dédiée à révolutionner l'accès aux services en Afrique
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-[#111111] rounded-2xl p-6 text-center border border-[#252525] hover:border-[#F97316]/30 transition-all duration-300 group"
              >
                {/* Image au lieu des initiales */}
                <div className="w-32 h-32 rounded-full mx-auto mb-4 overflow-hidden ring-2 ring-[#252525] group-hover:ring-[#F97316] transition-all duration-300">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <h3 className="font-semibold text-white text-base lg:text-lg">
                  {member.name}
                </h3>
                <p className="text-sm text-[#AAAAAA] mt-1">
                  {member.role}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION FAQ ============ */}
      
      {/* Hero FAQ */}
      <section className="relative bg-[#252525] text-white py-16 lg:py-20 overflow-hidden mt-12">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              <span className="text-sm font-medium">Foire Aux Questions</span>
            </motion.div>
            
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-4">
              Comment pouvons-nous <span className="text-white/90">vous aider ?</span>
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Tout ce que vous devez savoir sur VADOM
            </p>
          </motion.div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 115" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
                  fill="white"/>
          </svg>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20">
        <div className="container-custom max-w-4xl">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`bg-[#111111] rounded-2xl border transition-all duration-300 cursor-pointer
                  ${openIndex === index ? 'border-[#F97316]' : 'border-[#252525] hover:border-[#F97316]/30'}`}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <div className="flex items-center justify-between p-6">
                  <h3 className="font-semibold text-white pr-4 text-lg">
                    {faq.question}
                  </h3>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-[#F97316] flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`} 
                  />
                </div>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gradient-to-r from-transparent via-[#252525] to-transparent mb-4" />
                        <p className="text-[#AAAAAA] leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* CTA FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] rounded-2xl p-8 border border-[#252525]">
              <ChatBubbleLeftRightIcon className="h-12 w-12 text-[#F97316] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Vous n'avez pas trouvé votre réponse ?
              </h3>
              <p className="text-[#AAAAAA] mb-6">
                Notre équipe support est disponible 7j/7 pour vous aider
              </p>
              <Link to="/contact" className="inline-block bg-[#F97316] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#e8630a] transition-all">
                Nous contacter
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-[#252525] to-[#374151]/30">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              Prêt à rejoindre l'aventure ?
            </h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">
              Que vous soyez client ou prestataire, VADOM est là pour vous accompagner
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="bg-white text-[#F97316] px-8 py-3 rounded-full font-semibold hover:shadow-xl transition-all">
                Nous contacter
              </Link>
              <Link to="/become-smart" className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white/10 transition-all">
                Devenir Smart
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutWithFaqPage;